import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiResponse } from "@/types";
import type { ENUM_RECRUITMENT_INTERVIEW_STATUS } from "@/utils/enums/recruitment.enum";
import type {
  IInterviewRoom,
  IAiInterviewerAvatar,
  IAiInterviewSession,
  IInterview,
  IInterviewStats,
  IScheduleInterviewPayload,
  IScorecardDraft,
  ISubmitScorecardPayload,
} from "@/types/(company)/recruitment.interface";
import { companyRecruitmentApi } from "./api.recruitment";

export interface IListInterviewsParams {
  segment?: "upcoming" | "completed" | "cancelled" | "all";
  applicationId?: number;
  /** Candidate name, candidate email or job title. */
  search?: string;
  status?: ENUM_RECRUITMENT_INTERVIEW_STATUS;
}

/**
 * Tell the pipeline an interview changed.
 *
 * Interviews are their own `createApi`, so no `invalidatesTags` written here
 * can reach the candidate detail — the round card, the stage chip and the
 * "Feedback"/"Decision" steps all read the *application*, which lives in
 * `companyRecruitmentApi`. Completing a round or filing a scorecard therefore
 * left the candidate screen showing the state from before it happened.
 */
const invalidatePipeline = (dispatch: (action: unknown) => unknown) => {
  dispatch(
    companyRecruitmentApi.util.invalidateTags([
      "RecruitmentApplication",
      { type: "RecruitmentCandidate", id: "LIST" },
    ]),
  );
};

export const recruitmentInterviewsApi = createApi({
  reducerPath: "recruitmentInterviewsApi",
  baseQuery: dynamicBaseQuery,
  /*
    The call itself happens somewhere this app cannot see: the room opens in a
    new tab on Jobniq, and "Mark complete" or "Cancel" pressed in there goes
    straight to Jobniq, so nothing here invalidates. Coming back to this tab is
    when the answer may have changed, so that is when to re-read — otherwise a
    row read mid-call keeps "Live now" / "Rejoin" until a hard reload.
  */
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: ["Interview", "InterviewStats"],
  endpoints: (builder) => ({
    getInterviews: builder.query<IApiResponse<IInterview[]>, IListInterviewsParams | void>({
      query: (params) => ({ url: APIEndpoints.Interviews.Base, params: params ?? {} }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((iv) => ({ type: "Interview" as const, id: iv.id })),
              { type: "Interview" as const, id: "LIST" },
            ]
          : [{ type: "Interview" as const, id: "LIST" }],
    }),

    getInterview: builder.query<IApiResponse<IInterview>, number>({
      query: (id) => ({ url: APIEndpoints.Interviews.ById(id) }),
      providesTags: (_r, _e, id) => [{ type: "Interview" as const, id }],
    }),

    /**
     * Open the video room for a human-conducted round.
     *
     * A mutation rather than a query, and never cached — `keepUnusedDataFor: 0`
     * is deliberate. The response carries a meeting token, which is a
     * capability: whoever holds it walks into the room as this interviewer with
     * rights to mute and remove people. A cached one sitting in the RTK store
     * is a credential outliving the click that earned it.
     *
     * Invalidates nothing. Opening a room does not change the interview as far
     * as any list is concerned, and refetching the board on a join would move
     * rows under somebody who is about to talk to a candidate.
     */
    openInterviewRoom: builder.mutation<IApiResponse<IInterviewRoom>, number>({
      query: (id) => ({ url: APIEndpoints.Interviews.Room(id), method: "POST" }),
    }),

    getInterviewStats: builder.query<IApiResponse<IInterviewStats>, void>({
      query: () => ({ url: APIEndpoints.Interviews.Stats }),
      providesTags: [{ type: "InterviewStats", id: "STATS" }],
    }),

    scheduleInterview: builder.mutation<IApiResponse<IInterview>, IScheduleInterviewPayload>({
      query: (body) => ({ url: APIEndpoints.Interviews.Base, method: "POST", body }),
      invalidatesTags: [{ type: "Interview", id: "LIST" }, { type: "InterviewStats", id: "STATS" }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    rescheduleInterview: builder.mutation<
      IApiResponse<IInterview>,
      { id: number; scheduledAt?: string; durationMinutes?: number; aiDueAt?: string }
    >({
      query: ({ id, ...body }) => ({ url: APIEndpoints.Interviews.Reschedule(id), method: "PATCH", body }),
      // A new slot or due date moves the round between Today, Pending and Alerts.
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Interview", id },
        { type: "Interview", id: "LIST" },
        { type: "InterviewStats", id: "STATS" },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    /** Kanban drag-and-drop — a free move between the board's columns. */
    updateInterviewStatus: builder.mutation<
      IApiResponse<IInterview>,
      { id: number; status: ENUM_RECRUITMENT_INTERVIEW_STATUS }
    >({
      query: ({ id, status }) => ({
        url: APIEndpoints.Interviews.Status(id),
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Interview", id },
        { type: "Interview", id: "LIST" },
        { type: "InterviewStats", id: "STATS" },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    cancelInterview: builder.mutation<IApiResponse<IInterview>, number>({
      query: (id) => ({ url: APIEndpoints.Interviews.Cancel(id), method: "POST" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Interview", id },
        { type: "Interview", id: "LIST" },
        { type: "InterviewStats", id: "STATS" },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    completeInterview: builder.mutation<IApiResponse<IInterview>, number>({
      query: (id) => ({ url: APIEndpoints.Interviews.Complete(id), method: "POST" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Interview", id },
        { type: "Interview", id: "LIST" },
        { type: "InterviewStats", id: "STATS" },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    markInterviewReviewed: builder.mutation<IApiResponse<IInterview>, number>({
      query: (id) => ({ url: APIEndpoints.Interviews.Reviewed(id), method: "POST" }),
      // No stats tag: this only stamps `reviewedAt`, which no counter reads.
      invalidatesTags: (_r, _e, id) => [{ type: "Interview", id }, { type: "Interview", id: "LIST" }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    submitScorecard: builder.mutation<
      IApiResponse<unknown>,
      { id: number; body: ISubmitScorecardPayload }
    >({
      query: ({ id, body }) => ({ url: APIEndpoints.Interviews.Scorecard(id), method: "POST", body }),
      // The last panel scorecard completes the round server-side.
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Interview", id },
        { type: "Interview", id: "LIST" },
        { type: "InterviewStats", id: "STATS" },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    /** "Draft from my notes with AI" (#18) — the AI's first draft of the form; nothing is saved. */
    draftScorecard: builder.mutation<
      IApiResponse<IScorecardDraft>,
      { id: number; notes: string; locale?: "en" | "ar" }
    >({
      query: ({ id, notes, locale }) => ({
        url: APIEndpoints.Interviews.ScorecardDraft(id),
        method: "POST",
        body: { notes, locale },
      }),
    }),

    getAiSession: builder.query<IApiResponse<IAiInterviewSession>, number>({
      query: (id) => ({ url: APIEndpoints.Interviews.AiSession(id) }),
    }),

    /**
     * The AI interviewers this workspace can choose from.
     *
     * Reference data — the same for every tenant, and it changes on the
     * provider's schedule rather than ours, so it is cached for the session
     * instead of being re-fetched each time a picker opens. Its call sites
     * opt out of this api's refetch-on-focus for the same reason.
     */
    getAiInterviewerAvatars: builder.query<IApiResponse<IAiInterviewerAvatar[]>, void>({
      query: () => ({ url: APIEndpoints.Interviews.AiAvatars }),
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const {
  useGetInterviewsQuery,
  useGetInterviewQuery,
  useGetInterviewStatsQuery,
  useScheduleInterviewMutation,
  useRescheduleInterviewMutation,
  useUpdateInterviewStatusMutation,
  useCancelInterviewMutation,
  useCompleteInterviewMutation,
  useMarkInterviewReviewedMutation,
  useSubmitScorecardMutation,
  useDraftScorecardMutation,
  useGetAiSessionQuery,
  useGetAiInterviewerAvatarsQuery,
  useOpenInterviewRoomMutation,
} = recruitmentInterviewsApi;
